/**
 * A stable accent and texture per entity, from the eight audited label slots.
 *
 * Shared rather than defined beside one of its callers: the same workspace is
 * drawn on the list, in the shell rail, and (as its author) on a task card, and
 * an accent computed three ways is the drift nobody notices until a screenshot
 * shows two of them at once. The rail and the list had already drifted — the
 * list picked a slot, the rail was `bg-primary` — so a view transition between
 * them morphed a pastel square into a blue one while claiming to be the same
 * object twice (spec 075 FR-026).
 *
 * Every colour here is a semantic token, never a literal. That is not politeness
 * about `check-tokens.sh`; it is what makes the letter's contrast provable. All
 * eight slots are pale tints audited against `--label-chip-foreground`, so a
 * monogram drawn from any two of them clears AA by construction, whatever the
 * hash chooses.
 */

/** Stable 32-bit hash. From the id, never a name or a position (075 FR-028). */
export function hashOf(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/**
 * Listed statically, not built from a template string — Tailwind's build-time
 * scanner reads literal class names out of the source, so `bg-label-slot-${n}`
 * would compile to no CSS at all and every monogram would render transparent.
 * The same reason `TEXTURE_FILL_CLASSES` below is written out.
 */
const ACCENT_CLASSES = [
  'bg-label-slot-1 text-label-chip-foreground',
  'bg-label-slot-2 text-label-chip-foreground',
  'bg-label-slot-3 text-label-chip-foreground',
  'bg-label-slot-4 text-label-chip-foreground',
  'bg-label-slot-5 text-label-chip-foreground',
  'bg-label-slot-6 text-label-chip-foreground',
  'bg-label-slot-7 text-label-chip-foreground',
  'bg-label-slot-8 text-label-chip-foreground',
];

const TEXTURE_FILL_CLASSES = [
  'fill-label-slot-1',
  'fill-label-slot-2',
  'fill-label-slot-3',
  'fill-label-slot-4',
  'fill-label-slot-5',
  'fill-label-slot-6',
  'fill-label-slot-7',
  'fill-label-slot-8',
];

/** The surface colour, and the foreground already audited against it. */
export function accentFor(id: string): string {
  return ACCENT_CLASSES[hashOf(id) % ACCENT_CLASSES.length];
}

/**
 * The shapes' colour: a *different* slot from the background.
 *
 * `+ 1 + (…% 7)` rather than a plain modulo, because a texture that happened to
 * hash onto its own background would draw nothing and read as a bug in the
 * generator rather than as one workspace's luck.
 */
export function textureFillFor(id: string): string {
  const hash = hashOf(id);
  const base = hash % TEXTURE_FILL_CLASSES.length;
  const offset = 1 + ((hash >>> 5) % (TEXTURE_FILL_CLASSES.length - 1));
  return TEXTURE_FILL_CLASSES[(base + offset) % TEXTURE_FILL_CLASSES.length];
}
